// --- Imports ---

import { Flex } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { FC, useCallback, useEffect, useId, useMemo, useState } from "react";
import { shallowEqual } from "react-redux";
import {
    getKpiGetProgress,
    getKpiGetProgressThunk,
    getKpiGetSalaryToPay,
    getKpiGetSalaryToPayThunk,
} from "tm-shared/api";
import { backendDateFormat, displayDateFormat } from "tm-shared/constants";
import { MotivationGroupsInfo, SalaryToPaid, UserRole } from "tm-shared/types";

import { useAsyncTaskQueue } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
} from "@/hooks/redux";
import { ProgressLoader } from "@/shared/feedback";
import { AppDatePicker } from "@/shared/forms";
import {
    getKpiGetProgressSvThunk,
    getKpiGetSalaryToPaySvThunk,
} from "@/store/slices/manager";
import { setShouldUpdateKpi } from "@/store/slices/offline";
import { getKpiTargetDate } from "@/utils/business/getKpiTargetDate";

import { DashboardErrorBlock } from "../../components/DashboardErrorBlock";
import { DashboardInfoBlock } from "../../components/DashboardInfoBlock";
import { SelectedDateSalaryState } from "../../hooks/useSelectedDateSalary";
import { DashboardInfoModal } from "./components/DashboardInfoModal";
import { KPICollapse } from "./components/KPICollapse";
import { MMLCollapse } from "./components/MMLCollapse";
import { StyledSelfPlanWrapper } from "./Styled";
import type { SelfPlanModalState } from "./types";

// --- Types ---

interface SelfPlanProps {
    source: "manager" | "work";
    employeeId?: string;
    onSalaryDataChange?: (selectedDateSalary: SelectedDateSalaryState) => void;
}

export const SelfPlan: FC<SelfPlanProps> = ({
    source,
    employeeId: employeeIdProp,
    onSalaryDataChange,
}) => {
    // --- Store hooks ---

    const dispatch = useAppDispatch();

    const storeEmployeeId = useAppSelector((state) => state.user.employee?.id);
    const shouldUpdateKpi = useAppSelector(
        (state) => state.offline.shouldUpdateKpi,
    );
    const offlineMode = useOfflineModeSelector();

    const workData = useAppSelector(
        (state) => ({
            salary: state.work.salary,
            motivation: state.work.motivation,
            motivationError: state.work.motivationError,
            mmlStatisticsError: state.work.mmlStatisticsError,
        }),
        shallowEqual,
    );
    const managerData = useAppSelector(
        (state) => ({
            salary: state.manager.salary,
            motivation: state.manager.motivation,
            motivationError: state.manager.motivationError,
            mmlStatisticsError: state.manager.mmlStatisticsError,
        }),
        shallowEqual,
    );

    const kpiTargetDate = getKpiTargetDate();

    // --- Local state ---

    const [targetDate, setTargetDate] = useState(kpiTargetDate);
    const [localMotivation, setLocalMotivation] =
        useState<MotivationGroupsInfo | null>(null);
    const [localSalary, setLocalSalary] = useState<SalaryToPaid | null>(null);
    const [localMotivationError, setLocalMotivationError] = useState("");
    const [localSalaryError, setLocalSalaryError] = useState("");

    const employeeId = employeeIdProp ?? storeEmployeeId;

    const { queue, progress } = useAsyncTaskQueue();

    // --- Derived state ---

    const isKpiTargetDate = targetDate === kpiTargetDate;
    const isManagerSource = source === "manager";

    const {
        salary: storeSalary,
        motivation: storeMotivation,
        motivationError: storeMotivationError,
        mmlStatisticsError,
    } = isManagerSource ? managerData : workData;

    const motivation = isKpiTargetDate ? storeMotivation : localMotivation;
    const salary = isKpiTargetDate ? storeSalary : localSalary;
    const motivationError = isKpiTargetDate
        ? storeMotivationError
        : localMotivationError;

    const dashboardInfoModalKey = useId();
    const [modal, setModal] = useState<SelfPlanModalState | null>(null);

    // --- Memoized values ---

    const motivationGroups = useMemo(
        () =>
            motivation?.motivationGroups
                ? Object.values(motivation.motivationGroups).sort(
                      (a, b) => a.group.order - b.group.order,
                  )
                : [],
        [motivation?.motivationGroups],
    );

    // --- Modal handlers ---

    const handleOpenModal = useCallback((info: SelfPlanModalState) => {
        setModal(info);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModal(null);
    }, []);

    // --- Data handlers ---

    const handleUpdate = async (date: string = targetDate) => {
        if (!employeeId) return;

        const targetRole = isManagerSource ? UserRole.Sv : UserRole.Ta;
        const commonKpiParams = {
            employeeId,
            targetDate: date,
            roleSlug: targetRole,
        };

        const isCurrentTargetDate = date === kpiTargetDate;

        if (isCurrentTargetDate) {
            queue.add("Завантаження зарплати", () =>
                dispatch(
                    isManagerSource
                        ? getKpiGetSalaryToPaySvThunk(commonKpiParams)
                        : getKpiGetSalaryToPayThunk(commonKpiParams),
                ).unwrap(),
            );

            queue.add("Завантаження прогресу KPI", () =>
                dispatch(
                    isManagerSource
                        ? getKpiGetProgressSvThunk(commonKpiParams)
                        : getKpiGetProgressThunk(commonKpiParams),
                ).unwrap(),
            );

            try {
                await queue.runParallel();
            } catch (error) {
                console.error("Init error:", error);
            } finally {
                setTimeout(() => {
                    queue.clear();

                    dispatch(setShouldUpdateKpi(false));
                }, 2000);
            }
        } else {
            setLocalSalary(null);
            setLocalSalaryError("");
            setLocalMotivation(null);
            setLocalMotivationError("");

            queue.add("Завантаження зарплати", () =>
                getKpiGetSalaryToPay(commonKpiParams)
                    .then((res) => {
                        setLocalSalary(res.result);
                        setLocalSalaryError("");
                        return res.result;
                    })
                    .catch((err) => {
                        setLocalSalary(null);
                        setLocalSalaryError(
                            "Зарплатні дані наразі відсутні! Для уточнення зверніться до керівника",
                        );
                        throw err;
                    }),
            );

            queue.add("Завантаження прогресу KPI", () =>
                getKpiGetProgress(commonKpiParams)
                    .then((res) => {
                        setLocalMotivation(res.result);
                        setLocalMotivationError("");
                        return res.result;
                    })
                    .catch((err) => {
                        setLocalMotivation(null);
                        setLocalMotivationError(
                            "План мотивації наразі не розраховано! Для уточнення зверніться до керівника",
                        );
                        throw err;
                    }),
            );

            try {
                await queue.runParallel();
            } catch (error) {
                console.error("Local fetch error:", error);
            } finally {
                setTimeout(() => {
                    queue.clear();
                }, 2000);
            }
        }
    };

    const handleDateChange = (date: Dayjs | null) => {
        if (date && !offlineMode) {
            const formattedDate = date.format(backendDateFormat.date);
            setTargetDate(formattedDate);
            handleUpdate(formattedDate);
        }
    };

    // --- Effects ---

    useEffect(() => {
        if (onSalaryDataChange) {
            if (isKpiTargetDate) {
                onSalaryDataChange({
                    salary: null,
                    error: "",
                    isActive: false,
                });
            } else {
                onSalaryDataChange({
                    salary: localSalary,
                    error: localSalaryError,
                    isActive: true,
                });
            }
        }
        return () => {
            if (onSalaryDataChange) {
                onSalaryDataChange({
                    salary: null,
                    error: "",
                    isActive: false,
                });
            }
        };
    }, [isKpiTargetDate, localSalary, localSalaryError, onSalaryDataChange]);

    // --- UI helpers ---

    const makeDateHint = () => {
        const titles = [];

        if (motivation?.calculatedAt) {
            titles.push(
                `Оновлено: ${dayjs(motivation.calculatedAt).format(
                    displayDateFormat.dateTimeSeconds,
                )}`,
            );
        }

        if (motivation?.targetDate) {
            titles.push(
                `Продажі до: ${dayjs(motivation.targetDate).format(
                    displayDateFormat.date,
                )}`,
            );
        }

        return titles.length > 0
            ? titles.join("<br />")
            : "Ваші дані ще не прораховано";
    };

    // --- Render state ---

    const hasAllErrors = motivationError && mmlStatisticsError;
    const isLoaderOpen = progress.isRunning || progress.tasks.length > 0;

    // --- Render guards ---

    if (
        isKpiTargetDate &&
        !motivation &&
        !mmlStatisticsError &&
        !motivationError
    )
        return null;

    if (hasAllErrors) {
        return (
            <Flex vertical style={{ width: "100%" }}>
                <Flex vertical>
                    {motivationError && (
                        <DashboardErrorBlock title={motivationError} />
                    )}
                    {mmlStatisticsError && (
                        <DashboardErrorBlock title={mmlStatisticsError} />
                    )}
                </Flex>
            </Flex>
        );
    }

    // --- Render ---

    return (
        <>
            <Flex vertical style={{ width: "100%" }}>
                <StyledSelfPlanWrapper>
                    <DashboardInfoModal
                        modalKey={dashboardInfoModalKey}
                        isOpen={!!modal}
                        onClose={handleCloseModal}
                        modalState={modal}
                    />
                    <AppDatePicker
                        placeholder="Обрати дату"
                        value={dayjs(targetDate)}
                        format={"DD MMMM YYYY"}
                        onChange={handleDateChange}
                        allowClear={false}
                        disabledDate={(current: Dayjs) =>
                            current &&
                            current > dayjs(kpiTargetDate).endOf("day")
                        }
                        style={{ marginBottom: 12 }}
                        size="large"
                        disabled={offlineMode}
                    />
                    {motivationError ? (
                        <DashboardErrorBlock title={motivationError} />
                    ) : (
                        <>
                            <MMLCollapse
                                source={source}
                                motivation={motivation}
                                salary={salary}
                            />
                            {isKpiTargetDate && (
                                <DashboardInfoBlock
                                    title={makeDateHint()}
                                    onClickReload={() =>
                                        shouldUpdateKpi &&
                                        !offlineMode &&
                                        handleUpdate()
                                    }
                                />
                            )}

                            {motivationGroups.map((group) => (
                                <KPICollapse
                                    key={group.group.groupId}
                                    evaluatedMotivationGroup={group}
                                    setModalData={handleOpenModal}
                                />
                            ))}
                        </>
                    )}
                </StyledSelfPlanWrapper>
            </Flex>

            <ProgressLoader open={isLoaderOpen} tasks={progress.tasks} />
        </>
    );
};
